import { apiClient, ApiError } from '@/lib/api/client'
import { config } from '@/lib/config'

/** Mirrors `WinnerImportDtos.cs` (soly-web-api, Modules/Imports/Winner). */
export type WinnerImportAddress = {
  street: string
  number: string
  postalCode: string
  city: string
  country: string
}

export type WinnerImportCustomer = {
  winnerNumber: string | null
  displayName: string
  firstName: string | null
  lastName: string | null
  email: string | null
  phone: string | null
  vatNumber: string | null
  isCompany: boolean
  address: WinnerImportAddress | null
}

export type WinnerImportCandidateProject = {
  projectId: string
  name: string | null
  reference: string | null
  createdDate: string | null
  quoteCount: number
}

export type WinnerImportCustomerCandidate = {
  customerId: string
  name: string
  email: string | null
  phone: string | null
  city: string | null
  score: number
  evidence: string[]
  projects: WinnerImportCandidateProject[]
}

export type WinnerImportLine = {
  kind: string
  role: 'model' | 'article' | 'fee' | 'adjustment'
  position: string | null
  reference: string | null
  brand: string | null
  title: string
  description: string | null
  quantity: number | null
  unitPrice: number | null
  totalExVat: number | null
  vatPercent: number | null
}

export type WinnerImportTotals = { totalExVat: number; vatAmount: number; totalIncVat: number }

export type WinnerImportQuote = {
  offerNumber: string | null
  name: string
  offerDate: string | null
  articleCount: number
  suppliers: Array<{ name: string; lineCount: number; totalExVat: number }>
  lines: WinnerImportLine[]
  winnerTotals: WinnerImportTotals
  solyTotals: WinnerImportTotals
  isBalanced: boolean
}

export type WinnerImportPreview = {
  stopReason: string | null
  stopMessage: string | null
  file: {
    fileName: string
    documentKind: string
    dealerName: string | null
    winnerVersion: string | null
    exportedOn: string | null
  } | null
  customer: WinnerImportCustomer | null
  customerCandidates: WinnerImportCustomerCandidate[]
  project: {
    winnerNumber: string | null
    winnerName: string | null
    room: string | null
    suggestedName: string
    address: WinnerImportAddress | null
  } | null
  projectTypes: Array<{ id: string; label: string }>
  suggestedProjectTypeId: string | null
  entryProject: {
    projectId: string
    name: string | null
    reference: string | null
    customerId: string
    customerName: string | null
    customerDiffers: boolean
  } | null
  quote: WinnerImportQuote | null
  alreadyImported: WinnerImportAlreadyImported | null
  /** What Winner says that the quote deliberately does not carry (cash discount). */
  notices: string[]
  /** Where each Winner item group goes (SOLY-IMPORT-003 S08); null when the file was refused. */
  categories?: WinnerImportCategories | null
  /** How each Winner catalogue's lines are supplied (S12); null when the file was refused or the API predates S12. */
  supply?: WinnerImportSupply | null
}

/**
 * Mirrors `WinnerImportSupplyDto` (SOLY-IMPORT-003 S12). `needsReview` false: every catalogue answered.
 * `noPurchaseLines`: the lines set to « Aucun » without a question (not to order, or written by the import).
 */
export type WinnerImportSupply = {
  needsReview: boolean
  catalogs: WinnerImportSupplyCatalog[]
  suppliers: { id: string; name: string }[]
  noPurchaseLines: string[]
}

export type WinnerImportSupplyCatalog = {
  /** What the decision names the catalogue by: its code (« HACC5241 »). */
  key: string
  catalogName: string
  lineCount: number
  kind: 'mapped' | 'suggested' | 'unknown'
  mode: 'buy' | 'make' | 'none'
  supplierId: string | null
  supplierName: string | null
  /** The name « Créer » proposes when no supplier of the organisation matches. */
  newSupplierName: string | null
  needsReview: boolean
}

export type WinnerImportSupplyDecision = {
  key: string
  mode: 'buy' | 'make' | 'none'
  supplierId?: string | null
  /** Buy from a supplier the import creates at the commit, under this name. */
  createSupplierName?: string
  /** The review showed the catalogue answered: this is a deliberate change. */
  wasMapped?: boolean
}

/** Mirrors `WinnerImportCategoriesDto`. `needsReview` false: every group mapped, no question. */
export type WinnerImportCategories = {
  needsReview: boolean
  groups: WinnerImportCategoryGroup[]
  options: WinnerImportCategoryOption[]
}

export type WinnerImportCategoryGroup = {
  /** What the decision names the group by: « #20 » for a numbered group, « » for lines without one. */
  key: string
  groupNumber: string | null
  groupName: string | null
  lineCount: number
  totalExVat: number
  kind: 'mapped' | 'existing' | 'create' | 'none'
  categoryId: string | null
  categoryLabel: string | null
  newLabel: string | null
  /** The name the mapping was made on, when Winner now names the group otherwise. */
  renamedFrom: string | null
  targetHidden: boolean
  needsReview: boolean
}

export type WinnerImportCategoryOption = { id: string; label: string; isHidden: boolean }

export type WinnerImportCategoryDecision = {
  key: string
  mode: 'category' | 'create' | 'none'
  categoryId?: string
  newLabel?: string
  /** The review showed the group mapped: « create » is a deliberate change, not a race to reuse. */
  wasMapped?: boolean
}

/**
 * A quote this file may already be. `sameFile`: this exact file has a receipt and its commit is
 * refused (409); otherwise only a quote named after the same Winner offer. Empty ids when the
 * project is not the user's to open.
 */
export type WinnerImportAlreadyImported = {
  quoteId: string
  projectId: string
  projectName: string | null
  quoteName: string | null
  importedAt: string | null
  sameFile: boolean
  /** The latest standing version of that quote, and its status (`draft`, `issued`…). */
  versionId?: string | null
  versionStatus?: string | null
}

export type WinnerImportCustomerDecision = {
  mode: 'existing' | 'new'
  customerId?: string
  isCompany: boolean
  name: string
  firstName: string
  lastName: string
  email: string
  phone: string
  vatNumber: string
  address: WinnerImportAddress | null
}

export type WinnerImportProjectDecision = {
  mode: 'existing' | 'new'
  projectId?: string
  name: string
  projectTypeId: string | null
  jobSiteAddress: WinnerImportAddress | null
}

export type WinnerImportDecisions = {
  entryProjectId?: string
  customer?: WinnerImportCustomerDecision
  project?: WinnerImportProjectDecision
  /** One per group; a group left out takes the review's proposal. */
  categories?: WinnerImportCategoryDecision[]
  /** One per catalogue (S12); a catalogue left out takes the review's proposal. */
  supply?: WinnerImportSupplyDecision[]
}

export type WinnerImportResult = {
  customerId: string
  projectId: string
  quoteId: string
  versionId: string
  createdCustomer: boolean
  createdProject: boolean
  customerName: string | null
  projectName: string | null
  articleCount: number
  supplierCount: number
  totalIncVat: number
}

/**
 * Multipart posts bypass apiClient (which forces application/json), as `uploadProjectDocument`
 * does: the browser sets the boundary, the session cookie authenticates.
 */
async function postForm<T>(path: string, form: FormData, fallbackMessage: string): Promise<T> {
  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new ApiError(response.status, data.error?.code ?? 'IMPORT_FAILED', data.error?.message ?? fallbackMessage, data)
  }
  return (await response.json()) as T
}

export function previewWinnerImport(file: File, projectId?: string): Promise<WinnerImportPreview> {
  const form = new FormData()
  form.append('file', file)
  if (projectId) form.append('projectId', projectId)
  return postForm('/api/v2/imports/winner/preview', form, "Impossible de lire l'export Winner")
}

export function commitWinnerImport(file: File, decisions: WinnerImportDecisions): Promise<WinnerImportResult> {
  const form = new FormData()
  form.append('file', file)
  form.append('decisions', JSON.stringify(decisions))
  return postForm('/api/v2/imports/winner/commit', form, "Impossible de créer le dossier")
}

/** The 409 of a commit whose file was already imported carries the quote it became. */
export function alreadyImportedFrom(error: unknown): WinnerImportAlreadyImported | null {
  if (!(error instanceof ApiError) || error.code !== 'ALREADY_IMPORTED') return null
  const body = error.response as { alreadyImported?: WinnerImportAlreadyImported | null } | undefined
  return body?.alreadyImported ?? null
}

/** Mirrors `ImportReceiptDto`: the file a quote was imported from. */
export type ImportReceipt = { receiptId: string; fileName: string; fileSize: number; importedAt: string }

export async function getImportReceipt(quoteId: string): Promise<ImportReceipt | null> {
  try {
    return await apiClient.get<ImportReceipt>(`/api/v2/imports/winner/quotes/${quoteId}/receipt`)
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null
    throw error
  }
}

export function getImportReceiptFileUrl(quoteId: string): Promise<{ url: string; fileName: string }> {
  return apiClient.get<{ url: string; fileName: string }>(`/api/v2/imports/winner/quotes/${quoteId}/receipt/file`)
}

export type ClientLastProject = { projectId: string; reference: string; name: string | null; updatedAt: string | null }

export async function getClientLastProjects(customerId: string): Promise<ClientLastProject[]> {
  const response = await apiClient.get<{ items?: ClientLastProject[] | null }>(
    `/api/v2/clients/${customerId}/last-projects?take=10`
  )
  return response.items ?? []
}
